/**
 * The single authority for the invite sheet's role-dependent sharing header.
 *
 * Pure TypeScript, no React, no DOM, no SDK. A Commissioner (`owner` or
 * `admin` on the wire) reads only the `grant` source and a member reads only
 * the `shareLink` source; neither branch ever reads a field of the other.
 * That invariant is what keeps a Commissioner from ever being handed a
 * personal share link and an ordinary member from ever being handed the
 * pool's Invite code.
 */

export type typeInviteHeaderSource = {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
};

export type typeInviteGrantSource = typeInviteHeaderSource & {
  joinCode: string | null;
  joinUrl: string | null;
};

export type typeInviteShareLinkSource = typeInviteHeaderSource & {
  shareUrl: string | null;
  active: boolean;
};

export type typeInviteHeaderModel =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "sharingOff" }
  | { phase: "commissioner"; inviteCode: string; inviteUrl: string }
  | { phase: "member"; inviteUrl: string };

export function resolveInviteHeaderModel(input: {
  viewerPoolRole: "owner" | "admin" | "member";
  grant: typeInviteGrantSource;
  shareLink: typeInviteShareLinkSource;
}): typeInviteHeaderModel {
  const isCommissioner =
    input.viewerPoolRole === "owner" || input.viewerPoolRole === "admin";

  if (isCommissioner) {
    const { grant } = input;
    if (grant.isTransportError) return { phase: "failed" };
    if (grant.errorCode !== null) return { phase: "failed" };
    if (grant.isLoading) return { phase: "loading" };
    if (
      grant.joinCode === null ||
      grant.joinCode === "" ||
      grant.joinUrl === null ||
      grant.joinUrl === ""
    ) {
      return { phase: "failed" };
    }
    return {
      phase: "commissioner",
      inviteCode: grant.joinCode,
      inviteUrl: grant.joinUrl,
    };
  }

  const { shareLink } = input;
  if (shareLink.isTransportError) return { phase: "failed" };
  if (shareLink.errorCode !== null) return { phase: "failed" };
  if (shareLink.isLoading) return { phase: "loading" };
  if (!shareLink.active) return { phase: "sharingOff" };
  if (shareLink.shareUrl === null || shareLink.shareUrl === "") {
    return { phase: "failed" };
  }
  return { phase: "member", inviteUrl: shareLink.shareUrl };
}
